import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "../contexts/I18nContext";
import { parseDate } from "../lib/utils";

function Tile({ value }) {
  return (
    <div className="flip-tile w-[24px] h-[34px] sm:w-[28px] sm:h-[40px] flex items-center justify-center">
      <span className="font-mono-tab text-lg sm:text-2xl font-bold text-white leading-none">
        {value}
      </span>
    </div>
  );
}

function Unit({ n, label }) {
  const s = String(Math.max(0, n)).padStart(2, "0");
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex gap-0.5">
        <Tile value={s[0]} />
        <Tile value={s[1]} />
      </div>
      <span className="text-[8px] sm:text-[9.5px] font-bold tracking-[0.08em] uppercase text-white/50">{label}</span>
    </div>
  );
}

function Separator() {
  return (
    <span aria-hidden="true" className="font-mono-tab text-lg sm:text-2xl font-bold text-white/30 leading-none -translate-y-1 sm:-translate-y-2">
      :
    </span>
  );
}

export default function CountdownTimer({ target, onComplete }) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => new Date());
  const lastTriggeredRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const targetTime = target ? parseDate(target).getTime() : 0;
  const diff = targetTime ? Math.max(0, targetTime - now.getTime()) : 0;

  useEffect(() => {
    if (targetTime && targetTime <= now.getTime()) {
      if (lastTriggeredRef.current !== targetTime) {
        lastTriggeredRef.current = targetTime;
        if (onComplete) {
          onComplete();
        }
      }
    }
  }, [targetTime, now, onComplete]);

  if (!target) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  return (
    <div className="grain relative rounded-2xl bg-[#02371b] text-white overflow-hidden shadow-lg shadow-black/10" data-testid="countdown-timer">
      <div className="flex items-center justify-between px-3 pt-2.5 sm:px-4 sm:pt-3">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#41a42b] animate-pulse" />
          <span className="text-[8px] sm:text-[9px] font-bold tracking-[0.2em] uppercase text-white/60">{t("countdown")}</span>
        </div>
        <span className="text-[8px] sm:text-[9px] font-mono-tab text-white/40">LIVE</span>
      </div>
      <div className="flex items-center justify-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-3">
        <Unit n={days} label={t("days")} />
        <Separator />
        <Unit n={hours} label={t("hours")} />
        <Separator />
        <Unit n={mins} label={t("minutes")} />
        <Separator />
        <Unit n={secs} label={t("seconds")} />
      </div>
    </div>
  );
}
